import { AppThunkAction, AppThunkActionAsync } from 'store';

import i18n from 'i18n-js';

import agent from 'api/agent';
import {
    AddExistingDishModel,
    AddSectionModel,
    CatalogRestaurantDishModel,
    DeleteDishModel,
    DeleteSectionModel,
    DishCreateModel,
    RenameSectionModel,
    RestaurantCatalogDataType,
    RestaurantCatalogDataUpdateAction,
    SectionDailyMenuModel,
} from 'api/models';
import globalStore from 'store/globalStore';
import { MenuDetailsAction } from './actions';
import { DishData, SectionData } from './reducer';
import { Positions } from 'constants/enums';

function instanceOfDailyMenuSection(data: any): data is SectionDailyMenuModel {
    return 'price' in data && data.price > 0 && 'isCustomPrice' in data;
}

export const actionCreators = {
    getDetails:
        (
            id: string,
            position: string,
            withCustomPriceSections: boolean
        ): AppThunkActionAsync<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'MENU_GET_DETAILS_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            return agent.Menus.GetDetails(id, position, withCustomPriceSections)
                .then((response) => {
                    dispatch({
                        type: 'MENU_GET_DETAILS_SUCCESS',
                        menu: response,
                    });
                })
                .catch((e) => {
                    dispatch({
                        type: 'MENU_GET_DETAILS_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    renameSection:
        (id: string, model: RenameSectionModel): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'MENU_RENAME_SECTION_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Menus.RenameSection(id, model)
                .then((response) => {
                    dispatch({
                        type: 'MENU_RENAME_SECTION_SUCCESS',
                        menu: response,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'MENU_RENAME_SECTION_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    addNewItem:
        (model: DishCreateModel): AppThunkActionAsync<MenuDetailsAction> =>
        async (dispatch) => {
            dispatch({
                type: 'MENU_ADD_NEW_ITEM_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            try {
                const response = await agent.Menus.CreateDish(model);
                dispatch({
                    type: 'MENU_ADD_NEW_ITEM_SUCCESS',
                    dish: response,
                });
                dispatch({ type: 'MENU_ADD_NEW_DISH_SUCCESS', payload: response });
                dispatch({
                    type: 'ADD_DISH_TO_CATEGORIES_DISHES_SUCCESS',
                    payload: {
                        id: response.id,
                        name: response.name,
                        categoryId: response.categoryId,
                    },
                });
                globalStore.actionCreators.showToaster(
                    'success',
                    i18n.t('messages.createdSuccesfully')
                )(dispatch);
            } catch (e) {
                dispatch({
                    type: 'MENU_ADD_NEW_ITEM_ERROR',
                });
                globalStore.actionCreators.showToaster('error', e)(dispatch);
                throw e;
            } finally {
                globalStore.actionCreators.hideSpiner()(dispatch);
            }
        },
    updateItem:
        (
            model: DishCreateModel,
            id: string,
            sectionId: string
        ): AppThunkActionAsync<MenuDetailsAction> =>
        async (dispatch) => {
            dispatch({
                type: 'MENU_UPDATE_ITEM_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            try {
                const response = await agent.Menus.EditDish(model, id);
                response.sectionId = model.sectionId;
                dispatch({
                    type: 'MENU_UPDATE_ITEM_SUCCESS',
                    dish: response,
                    sectionId,
                });
                globalStore.actionCreators.showToaster(
                    'success',
                    i18n.t('messages.updatedSuccesfully')
                )(dispatch);
            } catch (e) {
                dispatch({
                    type: 'MENU_UPDATE_ITEM_ERROR',
                });
                globalStore.actionCreators.showToaster('error', e)(dispatch);
                throw e;
            } finally {
                globalStore.actionCreators.hideSpiner()(dispatch);
            }
        },
    addSection:
        (model: AddSectionModel | SectionDailyMenuModel): AppThunkAction<MenuDetailsAction> =>
        async (dispatch) => {
            globalStore.actionCreators.showSpiner()(dispatch);
            dispatch({
                type: 'MENU_SECTION_CREATE_START',
            });
            let result: SectionData | null = null;
            try {
                if (instanceOfDailyMenuSection(model)) {
                    result = await agent.Menus.AddDailyMenuSection(model as SectionDailyMenuModel);
                } else {
                    result = await agent.Menus.AddSection(model);
                }
                dispatch({
                    type: 'MENU_SECTION_CREATE_SUCCESS',
                    section: result!,
                });
            } catch (e: any) {
                dispatch({
                    type: 'MENU_SECTION_CREATE_ERROR',
                });
                globalStore.actionCreators.showToaster('error', e)(dispatch);
            } finally {
                globalStore.actionCreators.hideSpiner()(dispatch);
            }
        },
    deleteSection:
        (model: DeleteSectionModel): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'MENU_SECTION_DELETE_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Menus.DeleteSection(model)
                .then((response) => {
                    dispatch({
                        type: 'MENU_SECTION_DELETE_SUCCESS',
                        sectionId: response,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.deletedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'MENU_SECTION_DELETE_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    removeDishFromSection:
        (model: DeleteDishModel): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            globalStore.actionCreators.showSpiner()(dispatch);
            dispatch({ type: 'MENU_SECTION_REMOVE_DISH_START' });
            agent.Menus.RemoveDish(model)
                .then((response) => {
                    dispatch({
                        type: 'MENU_SECTION_REMOVE_DISH_SUCCESS',
                        dishId: response,
                        sectionId: model.sectionId,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.deletedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                    dispatch({ type: 'MENU_SECTION_REMOVE_DISH_ERROR' });
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    disableDish:
        (dish: DishData, sectionId: string): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'DISABLE_DISH_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Menus.DisableDish(dish.id)
                .then((response) => {
                    dispatch({
                        type: 'DISABLE_DISH_SUCCESS',
                        dish: response,
                        sectionId,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'DISABLE_DISH_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    deleteDish:
        (dishId: string, sectionId: string): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Menus.DeleteDish(dishId)
                .then((result) => {
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.deletedSuccesfully')
                    )(dispatch);
                    dispatch({ type: 'DELETE_DISH_SUCCESS', dishId: result });
                    dispatch({
                        type: 'REMOVE_DISH_FROM_CATEGORIES_DISHES_SUCCESS',
                        payload: result,
                    });
                })
                .catch((e) => globalStore.actionCreators.showToaster('error', e)(dispatch))
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    deleteCatalogMenuDish:
        (menuId: string, dishId: string): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Menus.DeleteMenuCatalogDish(menuId, dishId)
                .then((result) => {
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.deletedSuccesfully')
                    )(dispatch);
                    dispatch({ type: 'DELETE_DISH_SUCCESS', dishId: result });
                    dispatch({
                        type: 'REMOVE_DISH_FROM_CATEGORIES_DISHES_SUCCESS',
                        payload: result,
                    });
                })
                .catch((e) => globalStore.actionCreators.showToaster('error', e)(dispatch))
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    addExistingDish:
        (model: AddExistingDishModel): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'ADD_EXISTING_DISH_TO_SECTION_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Menus.AddExistingDish(model)
                .then(() => {
                    dispatch({
                        type: 'ADD_EXISTING_DISH_TO_SECTION_SUCCESS',
                        sectionId: model.sectionId,
                        dish: model.dish,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                    // actionCreators.getDetails(model.menuId)(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'ADD_EXISTING_DISH_TO_SECTION_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    copyDishFromCatalog:
        (model: DishCreateModel): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'MENU_ADD_NEW_ITEM_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Menus.CopyDishFromCatalog(model)
                .then((response) => {
                    dispatch({
                        type: 'MENU_ADD_NEW_ITEM_SUCCESS',
                        dish: response,
                    });
                    dispatch({ type: 'MENU_ADD_NEW_DISH_SUCCESS', payload: response });
                    dispatch({
                        type: 'ADD_DISH_TO_CATEGORIES_DISHES_SUCCESS',
                        payload: {
                            id: response.id,
                            name: response.name,
                            categoryId: response.categoryId,
                        },
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.createdSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'ADD_EXISTING_DISH_TO_SECTION_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    disableEnableSection:
        (id: string): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'DISABLE_ENABLE_SECTION_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Menus.DisableEnableDailyMenuSection(id)
                .then((res) => {
                    dispatch({
                        type: 'DISABLE_ENABLE_SECTION_SUCCESS',
                        payload: res,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'DISABLE_ENABLE_SECTION_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    editDailyMenuSection:
        (id: string, model: SectionDailyMenuModel): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'EDIT_DAILY_MENU_SECTION_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Menus.EditDailyMenuSection(id, model)
                .then((result) => {
                    dispatch({
                        type: 'EDIT_DAILY_MENU_SECTION_SUCCESS',
                        id: result.id,
                        name: result.name,
                        price: result.price,
                        priceLevels: result.priceLevels,
                        isCustomPrice: result.isCustomPrice,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'EDIT_DAILY_MENU_SECTION_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    reorderSections:
        (ids: string[], isDaily: boolean = false): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'MENU_SECTIONS_REORDER_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Menus.ReorderSections(ids, isDaily)
                .then((response) => {
                    dispatch({
                        type: 'MENU_SECTIONS_REORDER_SUCCESS',
                        sections: response,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'MENU_SECTIONS_REORDER_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    reorderDishes:
        (
            sectionId: string,
            ids: string[],
            isDaily: boolean = false
        ): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'MENU_SECTION_DISHES_REORDER_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Menus.ResorderDishes(sectionId, ids, isDaily)
                .then((response) => {
                    dispatch({
                        type: 'MENU_SECTION_DISHES_REORDER_SUCCESS',
                        dishes: response.dishes,
                        sectionId: response.sectionId,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'MENU_SECTION_DISHES_REORDER_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    setSectionColor:
        (id: string, isDaily: boolean, color: string): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'MENU_SECTION_SET_COLOR_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Menus.SetSectionColor(id, { color, isDaily })
                .then((response) => {
                    dispatch({
                        type: 'MENU_SECTION_SET_COLOR_SUCCESS',
                        sectionId: id,
                        color: response,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'MENU_SECTION_SET_COLOR_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    setSectionDishColor:
        (
            sectionId: string,
            dishId: string,
            isDaily: boolean,
            color: string
        ): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'MENU_SECTION_DISH_SET_COLOR_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Menus.SetSectionDishColor(sectionId, dishId, { color, isDaily })
                .then((response) => {
                    dispatch({
                        type: 'MENU_SECTION_DISH_SET_COLOR_SUCCESS',
                        sectionId,
                        dishId,
                        color: response,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'MENU_SECTION_DISH_SET_COLOR_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    copySectionDish:
        (sectionId: string, dishId: string): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'MENU_SECTION_DISH_COPY_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Menus.CopySectionDish(sectionId, dishId)
                .then((response) => {
                    dispatch({
                        type: 'MENU_SECTION_DISH_COPY_SUCCESS',
                        sectionId,
                        dish: response,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'MENU_SECTION_DISH_COPY_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    setSectionPhoto:
        (id: string, photo: string): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'MENU_SECTION_SET_PHOTO_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Menus.SetSectionPhoto(id, photo)
                .then((response) => {
                    dispatch({
                        type: 'MENU_SECTION_SET_PHOTO_SUCCESS',
                        sectionId: id,
                        photo: response,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'MENU_SECTION_SET_PHOTO_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    restaurantCatalogSectionToggleDisable:
        (
            restaurantId: string,
            position: Positions,
            section: SectionData
        ): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            agent.Catalog.RestaurantOverride(restaurantId, section.catalogData!.catalogId, {
                id: section.id,
                payload: '',
                dataType: RestaurantCatalogDataType.Section,
                action: RestaurantCatalogDataUpdateAction.DisableEnable,
            })
                .then(() => {
                    dispatch({
                        type: 'RESTAURANT_CATALOG_SECTION_TOGGLE_DISABLE_SUCCESS',
                        payload: section,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        section.isDisabled
                            ? i18n.t('catalog.disabledLocallyMessage')
                            : i18n.t('catalog.enabledLocallyMessage')
                    )(dispatch);
                })
                .catch((e) => {
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    restaurantCatalogDishToggleDisable:
        (restaurantId: string, dish: DishData): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            agent.Catalog.RestaurantOverride(restaurantId, dish.catalogData!.catalogId, {
                id: dish.id,
                payload: '',
                dataType: RestaurantCatalogDataType.Dish,
                action: RestaurantCatalogDataUpdateAction.DisableEnable,
            })
                .then(() => {
                    dispatch({
                        type: 'RESTAURANT_CATALOG_DISH_TOGGLE_DISABLE_SUCCESS',
                        payload: dish,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        dish.isDisabled
                            ? i18n.t('catalog.disabledLocallyMessage')
                            : i18n.t('catalog.enabledLocallyMessage')
                    )(dispatch);
                })
                .catch((e) => {
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    restaurantCatalogSetDishStockQuantity:
        (
            restaurantId: string,
            catalogId: string,
            dishId: string,
            quantity: number
        ): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            agent.Catalog.RestaurantOverride(restaurantId, catalogId, {
                id: dishId,
                payload: quantity.toString(),
                dataType: RestaurantCatalogDataType.Dish,
                action: RestaurantCatalogDataUpdateAction.ChangeAmount,
            })
                .then(() => {
                    dispatch({
                        type: 'RESTAURANT_CATALOG_DISH_SET_STOCK_QUANTITY_SUCCESS',
                        payload: { dishId, quantity },
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    restaurantCatalogSetDishWorkArea:
        (
            restaurantId: string,
            catalogId: string,
            dishId: string,
            workArea: string
        ): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            agent.Catalog.RestaurantOverride(restaurantId, catalogId, {
                id: dishId,
                payload: workArea,
                dataType: RestaurantCatalogDataType.Dish,
                action: RestaurantCatalogDataUpdateAction.ChangeWorkArea,
            })
                .then(() => {
                    dispatch({
                        type: 'RESTAURANT_CATALOG_DISH_SET_WORK_AREA_SUCCESS',
                        payload: { dishId, workArea },
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    restaurantMenuDetailsUpdateCatalogDish:
        (
            catalogId: string,
            restaurantId: string,
            dishId: string,
            dish: CatalogRestaurantDishModel
        ): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'RESTAURANT_CATALOG_DISH_UPDATE_MENU_DETAILS_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Catalog.UpdateRestaurantCatalogDishData(catalogId, restaurantId, dishId, dish)
                .then((result) => {
                    dispatch({
                        type: 'RESTAURANT_CATALOG_DISH_UPDATE_MENU_DETAILS_SUCCESS',
                        payload: result,
                        priceLevels: dish.priceLevels,
                    });
                })
                .catch((e) => {
                    dispatch({
                        type: 'RESTAURANT_CATALOG_DISH_UPDATE_MENU_DETAILS_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    restaurantMenuDetailsSetUpdatedDish:
        (dish: DishData): AppThunkAction<MenuDetailsAction> =>
        (dispatch) => {
            dispatch({
                type: 'RESTAURANT_CATALOG_DISH_UPDATE_MENU_DETAILS_SUCCESS',
                payload: dish,
            });
        },
};
